import * as React from 'react';
import {
  AlignJustify,
  AlignLeft,
  BadgeDollarSign,
  Heading as HeadingIcon,
  HelpCircle,
  Image as ImageIcon,
  LayoutGrid,
  Mail,
  MapPin,
  MousePointerClick,
  Quote,
  ShieldCheck,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import {
  blockCategoryLabel,
  blockTypeMeta,
  type BlockCategory,
  type BlockType,
  type BlockTypeMeta,
} from '@pageboss/shared';
import { Popover } from './popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface BlockPickerProps {
  open: boolean;
  anchorRect: DOMRect | null;
  onClose: () => void;
  onPick: (type: BlockType) => void;
  // Optional whitelist of block types — if provided, only those types
  // are shown. Used when the picker targets a column that can't host
  // every block type (e.g. a 1/3 column won't accept a hero).
  allowedTypes?: readonly BlockType[] | undefined;
}

const iconFor: Record<BlockType, React.ComponentType<{ className?: string }>> = {
  hero: Sparkles,
  heading: HeadingIcon,
  text: AlignLeft,
  cta: MousePointerClick,
  footer: AlignJustify,
  services: LayoutGrid,
  pricing: BadgeDollarSign,
  gallery: ImageIcon,
  team: Users,
  testimonials: Quote,
  about: User,
  contact: Mail,
  faq: HelpCircle,
  credentials: ShieldCheck,
  locations: MapPin,
};

export function BlockPicker({
  open,
  anchorRect,
  onClose,
  onPick,
  allowedTypes,
}: BlockPickerProps) {
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Reset query each time the picker opens.
  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  // Pre-filter by allowedTypes if a whitelist was passed in. The
  // empty-array case (`allowedTypes=[]`) is treated as "no types
  // allowed" — the picker still opens but shows the empty state.
  const baseSet = React.useMemo(() => {
    if (!allowedTypes) return blockTypeMeta;
    const allow = new Set(allowedTypes);
    return blockTypeMeta.filter((m) => allow.has(m.type));
  }, [allowedTypes]);

  // Flat filtered list (for arrow-key navigation), also grouped (for display).
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return baseSet;
    return baseSet.filter(
      (m) =>
        m.label.toLowerCase().includes(q) ||
        m.type.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q),
    );
  }, [baseSet, query]);

  const grouped = React.useMemo(() => {
    const map = new Map<BlockCategory, BlockTypeMeta[]>();
    for (const m of filtered) {
      const arr = map.get(m.category) ?? [];
      arr.push(m);
      map.set(m.category, arr);
    }
    // Stable order: structure → showcase → information
    const order: BlockCategory[] = ['structure', 'showcase', 'information'];
    return order
      .map((cat) => ({ category: cat, items: map.get(cat) ?? [] }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  React.useEffect(() => setActiveIndex(0), [query, open]);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const active = filtered[activeIndex];
      if (active) {
        onPick(active.type);
      }
    }
  }

  return (
    <Popover open={open} anchorRect={anchorRect} onClose={onClose}>
      <div className="flex max-h-[420px] flex-col" onKeyDown={onKeyDown}>
        <div className="border-b border-border-subtle p-3">
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search blocks…"
            aria-label="Search blocks"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {grouped.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-text-secondary">
              No blocks match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.category} className="mb-3 last:mb-0">
                <div className="px-3 pb-1 pt-2 eyebrow">
                  {blockCategoryLabel[group.category]}
                </div>
                <ul className="space-y-px">
                  {group.items.map((meta) => {
                    const Icon = iconFor[meta.type];
                    const flatIndex = filtered.indexOf(meta);
                    const isActive = flatIndex === activeIndex;
                    return (
                      <li key={meta.type}>
                        <button
                          type="button"
                          onClick={() => onPick(meta.type)}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          className={cn(
                            'flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors',
                            isActive
                              ? 'bg-brand-50 text-brand-700'
                              : 'hover:bg-surface-sunken',
                          )}
                        >
                          <Icon
                            className={cn(
                              'mt-0.5 h-5 w-5 shrink-0',
                              isActive ? 'text-brand-700' : 'text-text-secondary',
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-foreground">
                              {meta.label}
                            </div>
                            <div className="text-xs text-text-secondary">
                              {meta.description}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </Popover>
  );
}
